/**
 * Shared POI source-file helpers (IFC/RVT) used by LocationsPage and SystemsPage.
 * Extracted from the byte-identical copies in both pages (DUP-17).
 * Pure functions: the pages bind their loadedIfcFiles/loadingIfcFiles props locally.
 */

import { POI } from '../types';

/**
 * Check if a POI's source file (IFC or RVT) is currently loaded in the viewer.
 * @param poi - The POI to check
 * @param loadedIfcFiles - Array of currently loaded IFC/RVT filenames in viewer
 * @param fallbackSourceFile - Optional source file to use if POI doesn't have one (e.g., from parent location)
 */
export const isPOIIfcLoaded = (poi: POI | any, loadedIfcFiles?: string[], fallbackSourceFile?: string): boolean => {
  if (!loadedIfcFiles || loadedIfcFiles.length === 0) return false;

  // Check ifc_source_file field (top-level or in properties.location) - also check
  // source_file/rvt_source_file aliases (rvt_source_file added in E-12b so this deep
  // check is a strict superset of the shallow table-context check it replaced)
  const sourceFile = poi.ifc_source_file || poi.source_file || poi.rvt_source_file || poi.properties?.location || poi.properties?.source_file || fallbackSourceFile;
  if (sourceFile && loadedIfcFiles.includes(sourceFile)) return true;

  // Check custom_data for IFC or RVT originalFileName
  if (poi.custom_data) {
    try {
      const customData = typeof poi.custom_data === 'string'
        ? JSON.parse(poi.custom_data)
        : poi.custom_data;
      // Check IFC source file
      if (customData?.ifc?.originalFileName &&
          loadedIfcFiles.includes(customData.ifc.originalFileName)) {
        return true;
      }
      // Check RVT source file
      if (customData?.rvt?.originalFileName &&
          loadedIfcFiles.includes(customData.rvt.originalFileName)) {
        return true;
      }
      // Check RVT sourceFile (alternative field name)
      if (customData?.rvt?.sourceFile &&
          loadedIfcFiles.includes(customData.rvt.sourceFile)) {
        return true;
      }
      // FALLBACK: If this is an RVT POI without originalFileName, check if ANY .rvt file is loaded
      // This handles existing POIs that were extracted before the fix
      if (customData?.rvt && !customData.rvt.originalFileName && !customData.rvt.sourceFile) {
        const hasLoadedRvt = loadedIfcFiles.some(f => f.toLowerCase().endsWith('.rvt'));
        if (hasLoadedRvt) return true;
      }
    } catch { /* ignore parse errors */ }
  }

  // Check properties.metadata for IFC or RVT
  if (poi.properties?.metadata?.ifc?.originalFileName &&
      loadedIfcFiles.includes(poi.properties.metadata.ifc.originalFileName)) {
    return true;
  }
  if (poi.properties?.metadata?.rvt?.originalFileName &&
      loadedIfcFiles.includes(poi.properties.metadata.rvt.originalFileName)) {
    return true;
  }

  return false;
};

/**
 * Get a POI's source file name (IFC or RVT).
 * @param poi - The POI to check
 * @param fallbackSourceFile - Optional source file to use if POI doesn't have one (e.g., from parent location)
 */
export const getPOISourceFile = (poi: POI | any, fallbackSourceFile?: string): string | null => {
  // Check ifc_source_file field (top-level or in properties.location) - also check source_file alias
  const sourceFile = poi.ifc_source_file || poi.source_file || poi.properties?.location || poi.properties?.source_file;
  if (sourceFile) return sourceFile;

  // Check custom_data for IFC or RVT originalFileName
  if (poi.custom_data) {
    try {
      const customData = typeof poi.custom_data === 'string'
        ? JSON.parse(poi.custom_data)
        : poi.custom_data;
      // Check IFC source file
      if (customData?.ifc?.originalFileName) {
        return customData.ifc.originalFileName;
      }
      // Check RVT source file
      if (customData?.rvt?.originalFileName) {
        return customData.rvt.originalFileName;
      }
      // Check RVT sourceFile (alternative field name)
      if (customData?.rvt?.sourceFile) {
        return customData.rvt.sourceFile;
      }
    } catch { /* ignore parse errors */ }
  }

  // Check properties.metadata for IFC or RVT
  if (poi.properties?.metadata?.ifc?.originalFileName) {
    return poi.properties.metadata.ifc.originalFileName;
  }
  if (poi.properties?.metadata?.rvt?.originalFileName) {
    return poi.properties.metadata.rvt.originalFileName;
  }

  // Return fallback if provided
  return fallbackSourceFile || null;
};

/**
 * Check if a POI's IFC file is currently loading.
 * @param poi - The POI to check
 * @param loadingIfcFiles - Array of IFC/RVT filenames currently being loaded
 * @param fallbackSourceFile - Optional source file to use if POI doesn't have one
 */
export const isPOIIfcLoading = (poi: POI | any, loadingIfcFiles?: string[], fallbackSourceFile?: string): boolean => {
  if (!loadingIfcFiles || loadingIfcFiles.length === 0) return false;
  const sourceFile = getPOISourceFile(poi, fallbackSourceFile);
  return sourceFile ? loadingIfcFiles.includes(sourceFile) : false;
};
